// =============================================================================
// GROUNDING POLICY — the no-invention thresholds as DATA-IN-DB, keyed by surface.
//
// The CONFIG pond for the write-gate (the conversation distiller's no-invention
// seam — see docs/BUILD-PLAN-write-gate.md §3.4). One row = one surface's grounding
// thresholds, so the gate is tunable WITHOUT a deploy. The PURE DistillGroundingGate
// never reads this table; the route/DO resolves a row through GroundingPolicySource
// (epoch/TTL KV-cached, falls back to DEFAULT_GROUNDING_POLICY, never throws) and
// PASSES a GroundingPolicy into gateClaim — the gate stays pure.
//
// Cross-user / cross-surface (NO userId), keyed by `surface` so a surface's policy is
// stored once. A 'universal' baseline row is folded under the named surface row at
// read time. `version` is stamped into every source_ref.policyVersion the gate writes.
// Additive only (rule #11), applied via the Neon tool, mirrored here.
// =============================================================================

import { sql } from 'drizzle-orm';
import { boolean, integer, jsonb, pgTable, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const groundingPolicy = pgTable('grounding_policy', {
  id: id(),
  // The consumer key, e.g. 'conversation', 'role_match', or the 'universal' baseline.
  // UNIQUE → one policy per surface, folded over 'universal' at read time.
  surface: varchar('surface', { length: 24 }).notNull().unique(),
  // Strong-source title-trace floor (mirrors GroundingPolicy.minTitleHits).
  minTitleHits: integer('min_title_hits').notNull().default(1),
  // Evidence-trace floor (mirrors GroundingPolicy.minEvidenceHits).
  minEvidenceHits: integer('min_evidence_hits').notNull().default(1),
  // Non-stopword tokens below which a turn is content-free (a sign-off carries no claim).
  minSignalTokens: integer('min_signal_tokens').notNull().default(3),
  // Distinct corpus signal tokens below which a job FIT is undetermined (read-model returns
  // fit = null, never a fabricated 0). Mirrors GroundingPolicy.minCorpusSignalTokens; the 6 radar
  // axes ride the same floor. Read-model only — the stored fit_score + scorer write are untouched.
  minCorpusSignalTokens: integer('min_corpus_signal_tokens').notNull().default(6),
  // When set, a kpiValue must trace to the user turn verbatim before a card may bank.
  requireKpiVerbatim: boolean('require_kpi_verbatim').notNull().default(true),
  // Sign-off / acknowledgement filler words (STEMMED forms the shared tokenizer emits) that
  // count as content-free, so a sign-off ("you're all set, all the best") carries no claimable
  // signal. DATA-IN-DB: tunable WITHOUT a deploy. Folded over 'universal' at read time; the
  // pure gate builds its filler Set from policy.signoffFiller, never a module-level constant.
  signoffFiller: jsonb('signoff_filler').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  // Row toggle.
  active: boolean('active').notNull().default(true),
  // Stamped into every source_ref.policyVersion the gate writes.
  version: integer('version').notNull().default(1),
  // GATE-1 (GenerationGate.ts, Phase B / B-P4 "modes from policy, not code"): 'shadow'
  // evaluates + logs a verdict but serves the artifact unchanged; 'enforce' drops/nulls
  // a failing fact-claim. Additive (rule #11); defaults 'shadow' everywhere so a cold
  // table/row can never accidentally start enforcing. Folded exactly like every other
  // column above (surface row over 'universal' over the pure DEFAULT).
  mode: varchar('mode', { length: 16 }).notNull().default('shadow'),
  ...timestamps,
});
